import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { Upload, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAppContext } from '@/contexts/AppContext';

interface VideoUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVideoUploaded?: () => void;
}

const VideoUpload = ({ open, onOpenChange, onVideoUploaded }: VideoUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const { loadVideos, setHeroVideo } = useAppContext();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type.startsWith('video/')) {
        setFile(selectedFile);
      } else {
        toast({
          title: "Error",
          description: "Please select a video file",
          variant: "destructive"
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !title) {
      toast({
        title: "Error",
        description: "Please select a file and enter a title",
        variant: "destructive"
      });
      return;
    }

    if (!isAdmin) {
      toast({
        title: "Error",
        description: "You must be logged in as admin to upload videos",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    
    try {
      const fileName = `${Date.now()}-${file.name}`;
      
      const { error: uploadError } = await supabase.storage
        .from('videos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      // Get the public URL of the uploaded video
      const { data: urlData } = supabase.storage
        .from('videos')
        .getPublicUrl(fileName);

      // Set as hero video
      setHeroVideo(urlData.publicUrl);
      
      // Reload videos list
      await loadVideos();

      toast({
        title: "Success",
        description: "Video uploaded successfully and set as hero video"
      });
      
      setFile(null);
      setTitle('');
      onVideoUploaded?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Upload failed:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload video. Please check your connection and try again.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload Hero Video
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Video Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter video title"
            />
          </div>
          <div>
            <Label htmlFor="video">Video File</Label>
            <Input
              id="video"
              type="file"
              accept="video/*"
              onChange={handleFileChange}
            />
            {file && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <span>{file.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFile(null)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
          <Button
            onClick={handleUpload}
            disabled={uploading || !file || !title}
            className="w-full"
          >
            {uploading ? 'Uploading...' : 'Upload & Set as Hero Video'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoUpload;